import { useQuotaDetailOutletContext } from './OperatorQuotasDetail.tsx';
import {
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import {
    mandatoryFieldErrorMessage,
    maxFieldErrorMessage,
    minFieldErrorMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { useNavigate } from 'react-router-dom';
import { type JSX, useState } from 'react';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import Alert from '@mui/material/Alert';

export function OperatorQuotasReduction() {
    return (
        <OperatorRoutedModal
            backUrl={'..'}
            variant={ModalVariant.TINY}
            render={() => (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                    }}
                >
                    <Box
                        sx={theme => ({
                            padding: theme.spacing(1.875, 3),
                            color: theme.palette.common.white,
                            backgroundColor: theme.palette.blue.main,
                            fontSize: '26px',
                            fontWeight: 500,
                        })}
                    >
                        <Localized
                            de="Kontingent reduzieren"
                            fr="Réduire le contingent"
                            it="Ridurre il contingente"
                            en="Reduce quota"
                        />
                    </Box>
                    <OperatorQuotasReductionBody />
                </Box>
            )}
        />
    );
}

function OperatorQuotasReductionBody() {
    const {
        contractQuotaPartitionId,
        quotaConfigs,
        refetchQuota,
        refetchQuotaDetail,
    } = useQuotaDetailOutletContext();
    const [reduceQuotaLimitState, reduceQuotaLimit, resetReduceQuotaLimit] =
        useServerWrite<ReduceQuotaPayload, null, ReduceQuotaFailure>(
            ({ contractQuotaPartitionId }) => ({
                url: `/ui-api/operator-account/quotas/${contractQuotaPartitionId}/reduction`,
            }),
        );
    const [
        reductionExceedsQutoaWarningOpen,
        setReductionExceedsQutoaWarningOpen,
    ] = useState(false);
    const navigate = useNavigate();
    useServerSuccessEffect(reduceQuotaLimitState, () => {
        refetchQuota();
        refetchQuotaDetail();
        navigate('..');
    });
    useServerErrorEffect(reduceQuotaLimitState, (_, data) => {
        if (data?.problem === ReduceQuotaProblem.REDUCTION_EXCEEDS_QUOTA) {
            resetReduceQuotaLimit();
            setReductionExceedsQutoaWarningOpen(true);
        }
    });

    const { control, watch, handleSubmit } =
        useForm<ReduceQuotasFormFieldValues>({
            defaultValues: {
                [ReduceQuotasFormFields.validFrom]: null,
                [ReduceQuotasFormFields.validTo]: null,
                [ReduceQuotasFormFields.reductionCount]: null,
                [ReduceQuotasFormFields.remark]: null,
            },
        });
    // eslint-disable-next-line react-hooks/incompatible-library
    const validFrom = watch(ReduceQuotasFormFields.validFrom);

    const validTo = watch(ReduceQuotasFormFields.validTo);
    const minReduction = 1;
    const maxReduction = Math.min(...quotaConfigs.map(q => q.limit));
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const minimumMessage = useTranslation(minFieldErrorMessage(minReduction));
    const maximumMessage = useTranslation(maxFieldErrorMessage(maxReduction));
    return (
        <Stack spacing={2} sx={theme => ({ padding: theme.spacing(3) })}>
            <QuotasReductionErrorModal
                reduceQuotaLimitState={reduceQuotaLimitState}
            />
            <Stack
                spacing={2}
                direction={'row'}
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                }}
            >
                <Controller
                    name={ReduceQuotasFormFields.validFrom}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            label={
                                <Localized
                                    de="Gültig von"
                                    fr="Valable à partir du"
                                    it="Valida dal"
                                    en="Valid from"
                                />
                            }
                            value={field.value}
                            onChange={field.onChange}
                            minDate={DateTime.now().plus({
                                day: 1,
                            })}
                            maxDate={validTo ?? undefined}
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                    fullWidth: true,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />

                <Controller
                    name={ReduceQuotasFormFields.validTo}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            label={
                                <Localized
                                    de="Gültig bis"
                                    fr="Valide jusqu'à"
                                    it="Valido fino a"
                                    en="Valid Until"
                                />
                            }
                            value={field.value}
                            onChange={field.onChange}
                            minDate={validFrom ?? undefined}
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                    fullWidth: true,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />
            </Stack>
            <ReactHookFormTextField
                type="number"
                label={
                    <Localized
                        de="Limitreduktion"
                        fr="Réduction de limite"
                        it="Riduzione del limite"
                        en="Limit reduction"
                    />
                }
                control={control}
                name={ReduceQuotasFormFields.reductionCount}
                rules={{
                    min: {
                        value: minReduction,
                        message: minimumMessage,
                    },
                    max: {
                        value: maxReduction,
                        message: maximumMessage,
                    },
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                }}
                sx={{ width: '50%' }}
            />
            <ReactHookFormTextField
                label={
                    <Localized
                        de="Bemerkung"
                        fr="Remarque"
                        it="Osservazione"
                        en="Remark"
                    />
                }
                control={control}
                name={ReduceQuotasFormFields.remark}
            />
            <Stack direction={'row-reverse'} spacing={2}>
                <Button
                    variant="contained"
                    type="submit"
                    color={'primary'}
                    onClick={handleSubmit(formValues => {
                        reduceQuotaLimit({
                            contractQuotaPartitionId: contractQuotaPartitionId,
                            validFrom: formValues.validFrom!.toISODate()!,
                            validTo: formValues.validTo!.toISODate()!,
                            reductionCount: formValues.reductionCount!,
                            remark: formValues.remark,
                            forceOnQuotaUnavailability: false,
                        });
                    })}
                    disabled={
                        reduceQuotaLimitState.status == RequestStatus.PENDING
                    }
                >
                    <Localized
                        de="Speichern"
                        fr="Sauvegarder"
                        it="Salva"
                        en="Save"
                    />
                </Button>
                <Button
                    onClick={() => navigate('..')}
                    variant="outlined"
                    disabled={
                        reduceQuotaLimitState.status == RequestStatus.PENDING
                    }
                >
                    <Localized
                        de="Abbrechen"
                        fr="Annuler"
                        it="Annulla"
                        en="Cancel"
                    />
                </Button>
            </Stack>
            <ReductionExceedsQuotaDialog
                open={reductionExceedsQutoaWarningOpen}
                onCancel={() => setReductionExceedsQutoaWarningOpen(false)}
                onReduction={handleSubmit(formValues => {
                    reduceQuotaLimit({
                        contractQuotaPartitionId: contractQuotaPartitionId,
                        validFrom: formValues.validFrom!.toISODate()!,
                        validTo: formValues.validTo!.toISODate()!,
                        reductionCount: formValues.reductionCount!,
                        remark: formValues.remark,
                        forceOnQuotaUnavailability: true,
                    });
                    setReductionExceedsQutoaWarningOpen(false);
                })}
            />
        </Stack>
    );
}

function ReductionExceedsQuotaDialog({
    open,
    onCancel,
    onReduction,
}: {
    open: boolean;
    onCancel: () => void;
    onReduction: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Reduzieren"
                    fr="Réduire"
                    it="Ridurre"
                    en="Reduce"
                />
            }
            onConfirm={onReduction}
            onCancel={onCancel}
            title={
                <Localized
                    de="Kontingent reduzieren"
                    fr="Réduire le contingent"
                    it="Ridurre il contingente"
                    en="Reduce quota"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Schon ausgestellte Produkte überschreiten das Kontingent mit der Reduktion. Möchten Sie dennoch fortsetzen?"
                    fr="Les produits déjà émis dépassent le quota avec la réduction. Voulez-vous continuer quand même ?"
                    it="I prodotti già emessi superano il contingente con la riduzione. Vuoi continuare comunque?"
                    en="The already issued products exceed the quota with the reduction. Do you still want to continue?"
                />
            </p>
        </ConfirmationDialog>
    );
}

function QuotasReductionErrorModal({
    reduceQuotaLimitState,
}: {
    reduceQuotaLimitState: ServerRequestState<null, ReduceQuotaFailure>;
}): JSX.Element | null {
    if (reduceQuotaLimitState.status !== RequestStatus.ERROR) {
        return null;
    }
    switch (reduceQuotaLimitState.data?.problem ?? null) {
        case ReduceQuotaProblem.OVERLAPPING_REDUCTION:
            return (
                <Alert severity="error">
                    <Localized
                        de="Es gibt schon eine Reduzierung, die sich mit dem ausgewählten Zeitraum überschneidet."
                        fr="Il y a déjà une réduction qui se superpose à la période choisie."
                        it="Esiste già una riduzione che si sovrappone con il periodo selezionato."
                        en="There is already a reduction that overlaps with the selected period."
                    />
                </Alert>
            );
        case ReduceQuotaProblem.TOO_MANY_REDUCTIONS:
            return (
                <Alert severity="error">
                    <MaximumQuotasReductionText />
                </Alert>
            );
        case ReduceQuotaProblem.REDUCTION_EXCEEDS_QUOTA:
        case null:
            return (
                <Alert severity="error">
                    <Localized
                        de="Ein unerwarteter Fehler ist aufgetreten."
                        fr="Une erreur inattendue s'est produite."
                        it="Si è verificato un errore imprevisto."
                        en="An unexpected error has occurred."
                    />
                </Alert>
            );
    }
}

export function MaximumQuotasReductionText() {
    return (
        <Localized
            de="Es sind maximal drei Kontingentsreduktionen möglich."
            fr="Un maximum de trois réductions de quota est possible."
            it="Sono possibili al massimo tre riduzioni del contingente."
            en="A maximum of three quota reductions are allowed."
        />
    );
}

export const enum ReduceQuotasFormFields {
    validFrom = 'validFrom',
    validTo = 'validTo',
    reductionCount = 'reductionCount',
    remark = 'remark',
}

export type ReduceQuotasFormFieldValues = {
    [ReduceQuotasFormFields.validFrom]: DateTime | null;
    [ReduceQuotasFormFields.validTo]: DateTime | null;
    [ReduceQuotasFormFields.reductionCount]: number | null;
    [ReduceQuotasFormFields.remark]: string | null;
};

interface ReduceQuotaPayload {
    contractQuotaPartitionId: number;
    validFrom: string;
    validTo: string;
    reductionCount: number;
    remark: string | null;
    forceOnQuotaUnavailability: boolean;
}

interface ReduceQuotaFailure {
    problem: ReduceQuotaProblem;
}

enum ReduceQuotaProblem {
    OVERLAPPING_REDUCTION = 'OVERLAPPING_REDUCTION',
    REDUCTION_EXCEEDS_QUOTA = 'REDUCTION_EXCEEDS_QUOTA',
    TOO_MANY_REDUCTIONS = 'TOO_MANY_REDUCTIONS',
}
